import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { ExternalLink, FileText } from 'lucide-react'
import { marked } from 'marked'
import { gfmHeadingId } from 'marked-gfm-heading-id'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Footer } from '@/components/footer'
import { NavBar } from '@/components/nav-bar'

marked.use(gfmHeadingId({ prefix: '' }))

const RAW_API_BASE = import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:8787'
const API_BASE = RAW_API_BASE.replace(/\/+$/, '')
const RAW_URL = `${API_BASE}/connector-reference.md`

export const Route = createFileRoute('/api/docs')({
  component: ApiDocsPage,
  head: () => ({
    meta: [
      { title: 'API documentation — Beyond' },
      {
        name: 'description',
        content:
          'Build and ship a connector for cloud-erp — manifests, host verbs, OIDC, webhooks.',
      },
    ],
  }),
})

interface TocEntry {
  id: string
  text: string
  level: 2 | 3
}

function ApiDocsPage() {
  const [markdown, setMarkdown] = useState<string | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ctrl = new AbortController()
    setLoading(true)
    fetch(RAW_URL, { signal: ctrl.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.text()
      })
      .then((text) => {
        setMarkdown(text)
        setError(null)
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setError(err instanceof Error ? err : new Error('unknown_error'))
      })
      .finally(() => setLoading(false))
    return () => ctrl.abort()
  }, [])

  // The raw .md keeps its inline "## Contents" block for GitHub readers;
  // we strip it here because the sticky side TOC replaces it visually.
  const html = useMemo(() => {
    if (!markdown) return ''
    const stripped = markdown.replace(/## Contents\n[\s\S]*?(?=\n## )/, '')
    return marked.parse(stripped, { async: false }) as string
  }, [markdown])

  const articleRef = useRef<HTMLElement>(null)
  const [toc, setToc] = useState<TocEntry[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)

  useEffect(() => {
    if (!articleRef.current || !html) return
    const headings = articleRef.current.querySelectorAll<HTMLHeadingElement>('h2[id], h3[id]')
    setToc(
      Array.from(headings).map((h) => ({
        id: h.id,
        text: h.textContent ?? '',
        level: h.tagName === 'H2' ? 2 : 3,
      })),
    )
  }, [html])

  useEffect(() => {
    if (!articleRef.current) return
    const headings = articleRef.current.querySelectorAll<HTMLHeadingElement>('h2[id], h3[id]')
    if (headings.length === 0) return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length > 0) {
          setActiveId(visible[0]!.target.id)
        }
      },
      { rootMargin: '-15% 0% -70% 0%' },
    )
    for (const h of headings) observer.observe(h)
    return () => observer.disconnect()
  }, [html])

  return (
    <>
      <NavBar />
      <main className="flex min-h-screen flex-col">
        {/* ---- Header ---- */}
        <section className="relative overflow-hidden bg-[#08080c] pt-32 pb-12">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.2) 1px, transparent 0)',
              backgroundSize: '28px 28px',
            }}
          />
          <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"
            >
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1 text-xs font-medium text-white/50">
                  <FileText className="h-3 w-3" />
                  Developer reference
                </span>
                <h1 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  API documentation
                </h1>
                <p className="mt-3 max-w-2xl text-sm text-white/45 leading-relaxed">
                  Build and ship a connector for cloud-erp — manifests, host verbs, OIDC, webhooks.
                </p>
              </div>
              <a
                href={RAW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 self-start rounded-full border border-white/10 px-4 py-2 text-xs font-medium text-white/60 transition-colors hover:border-white/20 hover:text-white/90 sm:self-auto"
              >
                Open raw
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* ---- Docs body ---- */}
        <section className="relative flex-1 pb-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_1fr] lg:items-start">
              <nav
                aria-label="On this page"
                className="hidden lg:block sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pt-6"
              >
                {toc.length > 0 ? (
                  <>
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                      On this page
                    </div>
                    <ul className="space-y-0.5 text-sm">
                      {toc.map((entry) => (
                        <li key={entry.id}>
                          <a
                            href={`#${entry.id}`}
                            className={
                              'block py-1 border-l-2 transition-colors ' +
                              (entry.level === 3 ? 'pl-5 ' : 'pl-3 ') +
                              (activeId === entry.id
                                ? 'border-primary text-foreground font-medium'
                                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border')
                            }
                          >
                            {entry.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </nav>

              <div className="min-w-0 rounded-2xl border border-border bg-card/40 px-6 py-8 sm:px-10 sm:py-10 mt-6">
                {loading ? (
                  <output
                    aria-busy="true"
                    aria-label="Loading documentation"
                    className="block space-y-3"
                  >
                    <div className="h-7 w-1/3 animate-pulse rounded bg-muted" />
                    <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
                    <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
                    <div className="mt-6 h-32 w-full animate-pulse rounded bg-muted" />
                  </output>
                ) : error ? (
                  <div className="text-sm">
                    <p className="font-medium text-destructive">
                      Could not load the connector reference.
                    </p>
                    <p className="mt-1 text-muted-foreground">{error.message}</p>
                    <p className="mt-3 text-muted-foreground">
                      You can also read it directly on{' '}
                      <a
                        href="https://github.com/beyond/cloud-erp/blob/main/docs/connector-reference.md"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        GitHub
                      </a>
                      .
                    </p>
                  </div>
                ) : (
                  <article
                    ref={articleRef}
                    className="prose prose-sm dark:prose-invert max-w-none
                      prose-headings:scroll-mt-24
                      prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-lg
                      prose-h2:mt-10 prose-h2:pb-2 prose-h2:border-b prose-h2:border-border
                      prose-code:before:content-none prose-code:after:content-none
                      prose-code:rounded prose-code:bg-muted prose-code:px-1 prose-code:py-0.5
                      prose-code:text-foreground prose-code:font-normal
                      prose-pre:bg-muted prose-pre:text-foreground prose-pre:border prose-pre:border-border prose-pre:overflow-x-auto
                      prose-table:text-sm
                      prose-th:border prose-th:border-border prose-th:bg-muted prose-th:px-3 prose-th:py-2
                      prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2
                      prose-a:text-primary prose-a:font-medium prose-a:no-underline hover:prose-a:underline"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
